import React, { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileText, Loader2, Paperclip, X } from 'lucide-react';
import { uploadService } from '../services/upload';
import { prepararImagemParaEnvio } from '../utils/imagem';

interface UploadDeArquivosProps {
  /** Caminhos ja anexados, na ordem em que foram enviados. */
  valor: string[];
  onChange: (caminhos: string[]) => void;
  label?: string;
  type?: 'comprovante' | 'nota_fiscal' | 'documento';
  accept?: string;
  maxArquivos?: number;
  disabled?: boolean;
  /** Marca o campo com o asterisco de obrigatorio e explica a regra. */
  obrigatorio?: boolean;
}

/** Ultimo trecho do caminho, que e o nome do arquivo guardado. */
const nomeDoCaminho = (caminho: string): string => caminho.split('/').pop() || caminho;

/**
 * Anexa varios arquivos a um lancamento.
 *
 * Uma nota de servico vem em duas folhas e o abastecimento tem cupom fiscal e
 * comprovante de pagamento separados. Com um arquivo so, a pessoa mandava um e
 * perdia o resto, ou montava uma colagem no celular.
 *
 * O envio acontece na escolha, nao no submit do formulario: arquivo recusado
 * por tamanho ou tipo aparece na hora, e nao depois de preencher tudo. Cada
 * arquivo sobe sozinho — um que falhe nao leva junto os que deram certo.
 */
const UploadDeArquivos: React.FC<UploadDeArquivosProps> = ({
  valor,
  onChange,
  label = 'Comprovantes',
  type = 'comprovante',
  accept = '.jpg,.jpeg,.png,.pdf,.doc,.docx',
  maxArquivos = 10,
  disabled = false,
  obrigatorio = false,
}) => {
  const [enviando, setEnviando] = useState(0);
  const entrada = useRef<HTMLInputElement>(null);

  const escolher = async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;

    const escolhidos = Array.from(lista);
    const espaco = maxArquivos - valor.length;

    if (espaco <= 0) {
      toast.error(`Limite de ${maxArquivos} arquivos atingido.`);
      return;
    }
    if (escolhidos.length > espaco) {
      toast.error(`Só cabem mais ${espaco} arquivo(s). Os demais foram ignorados.`);
    }

    const aceitos = escolhidos.slice(0, espaco);
    setEnviando((n) => n + aceitos.length);

    // Sequencial de proposito: dez fotos de celular em paralelo saturam o link
    // de quem esta no 4G, e o servidor recusa por tamanho no meio do caminho.
    const novos: string[] = [];
    for (const escolhido of aceitos) {
      // Mesma conversao do portal: quem lanca pela gestao tambem anexa foto
      // tirada no celular.
      const arquivo = await prepararImagemParaEnvio(escolhido);

      if (!uploadService.validateFileType(arquivo)) {
        toast.error(`${arquivo.name}: tipo não permitido.`);
        setEnviando((n) => n - 1);
        continue;
      }
      if (!uploadService.validateFileSize(arquivo)) {
        toast.error(`${arquivo.name}: arquivo muito grande.`);
        setEnviando((n) => n - 1);
        continue;
      }

      try {
        const resposta = await uploadService.uploadFile(arquivo, type);
        if (resposta.data?.path) novos.push(resposta.data.path);
      } catch {
        toast.error(`${arquivo.name}: falha no envio.`);
      } finally {
        setEnviando((n) => n - 1);
      }
    }

    if (novos.length > 0) {
      onChange([...valor, ...novos]);
      toast.success(novos.length === 1 ? 'Arquivo anexado.' : `${novos.length} arquivos anexados.`);
    }

    // Sem isto, escolher o mesmo arquivo de novo nao dispara o onChange.
    if (entrada.current) entrada.current.value = '';
  };

  const remover = (caminho: string) => onChange(valor.filter((c) => c !== caminho));

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {obrigatorio && <span className="text-red-500">*</span>}{' '}
        <span className="text-gray-400 font-normal">({valor.length}/{maxArquivos})</span>
      </label>
      {obrigatorio && valor.length === 0 && (
        <p className="mb-2 text-xs text-gray-500">É obrigatório anexar pelo menos um comprovante.</p>
      )}

      {valor.length > 0 && (
        <ul className="mb-2 space-y-2">
          {valor.map((caminho) => (
            <li key={caminho}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-700 truncate" title={caminho}>
                {nomeDoCaminho(caminho)}
              </span>
              {!disabled && (
                <button type="button" onClick={() => remover(caminho)}
                  className="p-1 rounded text-gray-400 hover:text-red-600"
                  aria-label={`Remover ${nomeDoCaminho(caminho)}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={() => entrada.current?.click()}
        disabled={disabled || enviando > 0 || valor.length >= maxArquivos}
        className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-4 text-gray-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {enviando > 0 ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm font-medium">Enviando {enviando} arquivo(s)...</span>
          </>
        ) : (
          <>
            <Paperclip className="w-5 h-5" />
            <span className="text-sm font-medium">
              {valor.length === 0 ? 'Anexar arquivos' : 'Anexar mais'}
            </span>
          </>
        )}
      </button>

      <input
        ref={entrada}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => escolher(e.target.files)}
      />
    </div>
  );
};

export default UploadDeArquivos;
